import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLoading } from "../contexts/LoadingContext";
import { useSubscriptionContext } from "../contexts/SubscriptionContext";
import { useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { HourglassSpinnerWrapper } from "./HourglassSpinnerWrapper";
import { HelpResourcesMenu } from "./HelpResourcesMenu";
import { TourToast } from "./TourToast";
import { ProfileCompletionGuide } from "./ProfileCompletionGuide";
import "./MainContent.css";

interface JobSearchLayoutProps {
  children: React.ReactNode;
}

export const JobSearchLayout: React.FC<JobSearchLayoutProps> = ({
  children,
}) => {
  const { loading, isAuthenticated } = useAuth();
  const { loadingStates } = useLoading();
  const { isLoadingSubscription } = useSubscriptionContext();
  const location = useLocation();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }
    document.body.classList.add("has-sidebar");
    return () => {
      document.body.classList.remove("has-sidebar");
    };
  }, [isAuthenticated]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((open) => !open);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Job search is usable without signing in (Google search path is local).
  // Do not redirect to login or block on subscription for anonymous visitors.
  const isServicePlansPage = location.pathname === "/service-plans";
  const shouldShowLoading =
    isAuthenticated &&
    (loading || (!isServicePlansPage && isLoadingSubscription));

  const loadingState = React.useMemo(() => {
    if (loading) {
      return {
        title: "Signing you in...",
        subtitle: "Please wait while we prepare your dashboard",
      };
    }
    if (isLoadingSubscription) {
      return {
        title: "Loading your account...",
        subtitle: "Please wait while we load your wallet and preferences",
      };
    }
    return { title: "Loading...", subtitle: "Please wait..." };
  }, [loading, isLoadingSubscription]);

  if (shouldShowLoading) {
    return (
      <HourglassSpinnerWrapper
        fullPage={true}
        title={loadingState.title}
        subtitle={loadingState.subtitle}
      />
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="main-content main-content--guest-job-search">
        <main className="main-body">{children}</main>
      </div>
    );
  }

  return (
    <div className="main-content">
      <HelpResourcesMenu />
      <TourToast />
      <ProfileCompletionGuide />

      {isMobileMenuOpen && (
        <div
          className="sidebar-overlay"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}
      <Sidebar
        loadingStates={loadingStates}
        isMobileMenuOpen={isMobileMenuOpen}
        onCloseMobileMenu={closeMobileMenu}
      />

      <div className="main-layout job-search-layout">
        <button
          className="mobile-menu-button"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
          type="button"
        >
          {isMobileMenuOpen ? "✕" : "☰"}
        </button>
        <main className="main-body">{children}</main>
      </div>
    </div>
  );
};
